import {
  Badge,
  Button,
  Col,
  FloatingToolbar,
  Label,
  MultiSelectFilter,
  Pagination,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  SearchField,
  TextField,
  TinyTable,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import {
  LucideChevronDown,
  LucideInfo,
  LucideMail,
  LucidePhone,
  LucidePlusCircle,
  LucidePowerOff,
  LucideTrash2,
  LucideUser,
  LucideUserPlus,
  LucideX,
} from 'lucide-react';

import { ConfirmationDialog } from '../_shared/ConfirmationDialog';

import { useBulkUserActions } from './_hooks/useBulkUserActions';
import { useUsersTableFilters } from './_hooks/useUsersTableFilters';
import { AddUserDialog } from './AddUserDialog';
import { UserRecord } from './types';
import { UserRowMenu } from './UserRowMenu';

import {
  FacilitiesCell,
  SettingsContent,
  UserStatusBadge,
} from '~/modules/settings/_components/_shared';
import { $openAddUserDialog } from '~/modules/settings/_state/addUserDialog.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { PermissionGate } from '~/shared/components/PermissionGate';

export const UsersTabView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useSettingsLocation();

  const filters = useUsersTableFilters();
  const actions = useBulkUserActions(filters.allUsers);

  const openAddUserDialog = useSetAtom($openAddUserDialog);

  const handleAddUser = () => {
    openAddUserDialog();
  };

  const handleEdit = (user: UserRecord) => {
    setLocation(`/team-and-permissions/users/${user.id}` as never);
  };

  const handleResendInvite = (_user: UserRecord) => {
    toast.info('Not implemented');
  };

  return (
    <>
      <SettingsContent className="@UsersTabView h-full">
        <Col className="h-full gap-4">
          <Row gap2>
            <SearchField
              value={filters.searchTerm}
              onChange={filters.handleSearchChange}
              placeholder={t.teamAndPermissions.search.placeholder}
              autoFocus
            />
            <PopoverTrigger>
              <Button variant="outline" color="neutral" className="gap-1.5">
                {t.teamAndPermissions.users.filters.allFacilities}
                {filters.facilityFilter.length > 0 && (
                  <Badge color="primary" size="small">
                    {filters.facilityFilter.length}
                  </Badge>
                )}
                <LucideChevronDown className="size-4 text-neutral/40" />
              </Button>
              <Popover placement="bottom start">
                <PopoverDialog>
                  {({ close }: { close: () => void }) => (
                    <MultiSelectFilter
                      className="min-w-[280px]"
                      entries={filters.facilities}
                      selectedEntries={filters.selectedFacilities}
                      selectedKeys={filters.selectedFacilityKeys}
                      onSelectionChange={filters.handleFacilityFilterChange}
                      getEntryKey={(f) => f.id}
                      getEntryLabel={(f) => f.name}
                      onReset={filters.handleFacilityFilterReset}
                      onDismiss={close}
                      searchPlaceholder={
                        t.teamAndPermissions.search.placeholder
                      }
                    />
                  )}
                </PopoverDialog>
              </Popover>
            </PopoverTrigger>
            <PopoverTrigger>
              <Button variant="outline" color="neutral" className="gap-1.5">
                {t.teamAndPermissions.users.filters.allRoles}
                {filters.rolesFilter.length > 0 && (
                  <Badge color="primary" size="small">
                    {filters.rolesFilter.length}
                  </Badge>
                )}
                <LucideChevronDown className="size-4 text-neutral/40" />
              </Button>
              <Popover placement="bottom start">
                <PopoverDialog>
                  {({ close }: { close: () => void }) => (
                    <MultiSelectFilter
                      className="min-w-[280px]"
                      entries={filters.roles}
                      selectedEntries={filters.selectedRoles}
                      selectedKeys={filters.selectedRoleKeys}
                      onSelectionChange={filters.handleRolesFilterChange}
                      getEntryKey={(r) => r.id}
                      getEntryLabel={(r) => r.name}
                      onReset={filters.handleRolesFilterReset}
                      onDismiss={close}
                      searchPlaceholder={
                        t.teamAndPermissions.search.placeholder
                      }
                    />
                  )}
                </PopoverDialog>
              </Popover>
            </PopoverTrigger>
            <PermissionGate action="userCreate">
              <Button
                className="ml-auto"
                icon={<LucidePlusCircle />}
                onPress={handleAddUser}
              >
                {t.teamAndPermissions.actions.addUser}
              </Button>
            </PermissionGate>
          </Row>

          <TinyTable<UserRecord>
            rowKey={(row) => row.id}
            selectable
            selectedKeys={actions.selectedKeys}
            onSelectionChange={actions.handleSelectionChange}
            isLoading={filters.isLoading}
            error={filters.error ?? undefined}
            sorting={filters.sorting}
            onSortChange={filters.handleSortChange}
            onRowClick={(row) => handleEdit(row)}
            columns={[
              {
                key: 'name',
                label: t.teamAndPermissions.users.table.columns.name,
                icon: LucideUser,
                sortable: true,
                cell: (row) => (
                  <Col className="gap-0.5">
                    <span
                      className={
                        row.status === 'archived'
                          ? 'text-neutral/50 line-through'
                          : ''
                      }
                    >
                      {row.name}
                    </span>
                    {row.roles.length > 0 && (
                      <span className="text-xs text-neutral/50">
                        {row.roles.join(', ')}
                      </span>
                    )}
                  </Col>
                ),
              },
              {
                key: 'contactInfo',
                label: t.teamAndPermissions.users.table.columns.contactInfo,
                icon: LucideMail,
                sortable: true,
                cell: (row) => (
                  <Col className="gap-0.5">
                    <Row className="items-center gap-1.5">
                      <LucideMail className="size-3 text-neutral/50" />
                      <span className="truncate">{row.email || '-'}</span>
                    </Row>
                    {row.phoneNumber && (
                      <Row className="items-center gap-1.5">
                        <LucidePhone className="size-3 text-neutral/50" />
                        <span>{row.phoneNumber}</span>
                      </Row>
                    )}
                  </Col>
                ),
              },
              {
                key: 'facilities',
                label: t.teamAndPermissions.users.table.columns.facilities,
                width: 200,
                sortable: true,
                cell: (row) => <FacilitiesCell facilities={row.facilities} />,
              },
              {
                key: 'status',
                label: t.teamAndPermissions.users.table.columns.status,
                width: 120,
                sortable: true,
                cell: (row) => <UserStatusBadge status={row.status} />,
              },
              {
                key: 'actions',
                label: '',
                width: 60,
                preventRowClick: true,
                cell: (row) => (
                  <UserRowMenu
                    user={row}
                    onEdit={() => handleEdit(row)}
                    onResendInvite={() => handleResendInvite(row)}
                    onRemoveAccess={() => actions.handleRemoveAccess(row)}
                    onRestoreAccess={() => actions.handleRestoreAccess(row)}
                    onDelete={() => actions.handleDelete(row)}
                    isDisabled={actions.actingOnUserId !== null}
                  />
                ),
              },
            ]}
            data={filters.paginatedUsers}
          />

          <FloatingToolbar visible={actions.selectedUserIds.length > 0}>
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-2 text-sm">
                <Badge color="primary" size="small">
                  {actions.selectedUserIds.length}
                </Badge>{' '}
                {t.teamAndPermissions.users.selected}
              </div>
              <div className="flex flex-row items-center gap-2">
                <PermissionGate action="userUpdate">
                  {actions.activeUserIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="small"
                      icon={<LucidePowerOff className="size-4" />}
                      onPress={actions.handleBulkRemoveAccess}
                      isDisabled={actions.isBulkActionRunning}
                    >
                      {t.teamAndPermissions.users.actions.removeAccess} (
                      {actions.activeUserIds.length})
                    </Button>
                  )}
                  {actions.archivedUserIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="small"
                      icon={<LucideUserPlus className="size-4" />}
                      onPress={actions.handleBulkRestoreAccess}
                      isDisabled={actions.isBulkActionRunning}
                    >
                      {t.teamAndPermissions.users.actions.restoreAccess} (
                      {actions.archivedUserIds.length})
                    </Button>
                  )}
                </PermissionGate>
                <PermissionGate action="userDelete">
                  <Button
                    variant="outline"
                    size="small"
                    color="failure"
                    icon={<LucideTrash2 className="size-4" />}
                    onPress={actions.handleBulkDelete}
                    isDisabled={actions.isBulkActionRunning}
                  >
                    {t.teamAndPermissions.users.actions.deleteUser} (
                    {actions.selectedUserIds.length})
                  </Button>
                </PermissionGate>
                <LucideX
                  className="size-4 cursor-pointer text-neutral/80"
                  onClick={actions.handleClearSelection}
                />
              </div>
            </div>
          </FloatingToolbar>

          {filters.total > 0 && (
            <Row className="justify-end">
              <Pagination
                limit={filters.limit}
                offset={filters.offset}
                total={filters.total}
                onChange={filters.handlePaginationChange}
              />
            </Row>
          )}
        </Col>
      </SettingsContent>
      <AddUserDialog />

      <ConfirmationDialog
        isOpen={actions.pendingAction?.type === 'remove'}
        onOpenChange={(open) => {
          if (!open) actions.closePendingAction();
        }}
        title={t.teamAndPermissions.users.dialogs.removeAccess.title(
          actions.pendingAction?.user.name ?? '',
        )}
        description={t.teamAndPermissions.users.dialogs.removeAccess.description(
          actions.pendingAction?.user.name ?? '',
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.removeAccess.remove}
        cancelLabel={t.teamAndPermissions.users.dialogs.removeAccess.discard}
        onConfirm={actions.handleConfirmRemoveAccess}
        onCancel={actions.closePendingAction}
        isLoading={actions.isRemoveAccessPending}
      />

      <ConfirmationDialog
        isOpen={actions.pendingAction?.type === 'restore'}
        onOpenChange={(open) => {
          if (!open) actions.closePendingAction();
        }}
        title={t.teamAndPermissions.users.dialogs.restoreAccess.title(
          actions.pendingAction?.user.name ?? '',
        )}
        description={t.teamAndPermissions.users.dialogs.restoreAccess.description(
          actions.pendingAction?.user.name ?? '',
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.restoreAccess.restore}
        cancelLabel={t.teamAndPermissions.users.dialogs.restoreAccess.discard}
        onConfirm={actions.handleConfirmRestoreAccess}
        onCancel={actions.closePendingAction}
        isLoading={actions.isRestoreAccessPending}
        confirmColor="primary"
        icon={
          <LucideInfo className="size-5 shrink-0 fill-primary text-white" />
        }
      />

      <ConfirmationDialog
        isOpen={actions.pendingAction?.type === 'delete'}
        onOpenChange={(open) => {
          if (!open) actions.closePendingAction();
        }}
        title={t.teamAndPermissions.users.dialogs.deleteAccount.title(
          actions.pendingAction?.user.name ?? '',
        )}
        description={t.teamAndPermissions.users.dialogs.deleteAccount.description(
          actions.pendingAction?.user.name ?? '',
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.deleteAccount.delete}
        cancelLabel={t.teamAndPermissions.users.dialogs.deleteAccount.discard}
        onConfirm={actions.handleConfirmDelete}
        onCancel={actions.closePendingAction}
        isLoading={actions.isDeletePending}
        confirmDisabled={
          actions.deleteConfirmName.trim() !==
          (actions.pendingAction?.user.name ?? '')
        }
      >
        <div className="space-y-2">
          <Label>
            {
              t.teamAndPermissions.users.dialogs.deleteAccount
                .teamMemberNameLabel
            }
          </Label>
          <TextField
            value={actions.deleteConfirmName}
            onChange={actions.setDeleteConfirmName}
            placeholder={actions.pendingAction?.user.name ?? ''}
            isDisabled={actions.isDeletePending}
            autoComplete="off"
          />
        </div>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={actions.pendingBulkAction === 'remove'}
        onOpenChange={(open) => {
          if (!open) actions.closePendingBulkAction();
        }}
        title={t.teamAndPermissions.users.dialogs.removeAccess.title(
          `${actions.selectedUserIds.length} users`,
        )}
        description={t.teamAndPermissions.users.dialogs.removeAccess.description(
          `${actions.selectedUserIds.length} users`,
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.removeAccess.remove}
        cancelLabel={t.teamAndPermissions.users.dialogs.removeAccess.discard}
        onConfirm={actions.handleConfirmBulkRemoveAccess}
        onCancel={actions.closePendingBulkAction}
        isLoading={actions.isBulkRemoveAccessPending}
      />

      <ConfirmationDialog
        isOpen={actions.pendingBulkAction === 'restore'}
        onOpenChange={(open) => {
          if (!open) actions.closePendingBulkAction();
        }}
        title={t.teamAndPermissions.users.dialogs.restoreAccess.title(
          `${actions.selectedUserIds.length} users`,
        )}
        description={t.teamAndPermissions.users.dialogs.restoreAccess.description(
          `${actions.selectedUserIds.length} users`,
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.restoreAccess.restore}
        cancelLabel={t.teamAndPermissions.users.dialogs.restoreAccess.discard}
        onConfirm={actions.handleConfirmBulkRestoreAccess}
        onCancel={actions.closePendingBulkAction}
        isLoading={actions.isBulkRestoreAccessPending}
        confirmColor="primary"
        icon={
          <LucideInfo className="size-5 shrink-0 fill-primary text-white" />
        }
      />

      <ConfirmationDialog
        isOpen={actions.pendingBulkAction === 'delete'}
        onOpenChange={(open) => {
          if (!open) actions.closePendingBulkAction();
        }}
        title={t.teamAndPermissions.users.dialogs.deleteAccount.title(
          `${actions.selectedUserIds.length} users`,
        )}
        description={t.teamAndPermissions.users.dialogs.deleteAccount.description(
          `${actions.selectedUserIds.length} users`,
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.deleteAccount.delete}
        cancelLabel={t.teamAndPermissions.users.dialogs.deleteAccount.discard}
        onConfirm={actions.handleConfirmBulkDelete}
        onCancel={actions.closePendingBulkAction}
        isLoading={actions.isDeletePending}
      />
    </>
  );
};
